import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Modal, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { pw9233Api } from '../../services/pw9233Api';
import { usePW9233Mode } from './hooks/usePW9233Mode';
import { usePW9233Record } from './hooks/usePW9233Record';
import { usePW9233Navigation, parseKey } from './hooks/usePW9233Navigation';
import { usePW9233Lookup } from './hooks/usePW9233Lookup';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import ActionBar from './components/ActionBar';
import MasterInfo from './components/MasterInfo';
import DetailGrid from './components/DetailGrid';
import './PW9233Form.css';

const PW9233Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.PW9233);

  // Hooks
  const { mode, setMode, isEditing } = usePW9233Mode();
  const { names, resolveSingleName, resolveAllNames, clearNames } = usePW9233Lookup();
  const {
    record, formData, details, loading,
    loadRecord, setField, getField,
    updateDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePW9233Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (empCode: string, yearMonth: string) => {
    const result = await loadRecord(empCode, yearMonth);
    if (result) {
      resolveAllNames(result, result.details?.[0]);
    }
  }, [loadRecord, resolveAllNames]);

  const { allKeys, currentIdx, setCurrentIdx, loadList, navTo } = usePW9233Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const keys = await loadList();
        if (keys.length > 0) {
          setCurrentIdx(0);
          const [empCode, yearMonth] = parseKey(keys[0]);
          await loadRecordWithNames(empCode, yearMonth);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Handlers --

  const handleAdd = useCallback(() => {
    if (isEditing) {
      handleSave(mode, (result) => {
        applyResult(result);
        setMode('browse');
        loadList().then(keys => {
          const key = `${result.f9430_01}|${result.f9430_02}`;
          const idx = keys.indexOf(key);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      clearNames();
      resetForAdd();
      setMode('add');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode, clearNames, resetForAdd, loadList, setCurrentIdx]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteRecord = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除員工「${record.f9430_01}」年月「${record.f9430_02}」的薪資記錄？所有明細將一併刪除。`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const keys = await loadList();
          if (keys.length > 0) {
            const newIdx = Math.min(currentIdx, keys.length - 1);
            setCurrentIdx(newIdx);
            const [empCode, yearMonth] = parseKey(keys[newIdx]);
            await loadRecordWithNames(empCode, yearMonth);
          } else {
            setCurrentIdx(-1);
            clearNames();
            resetForAdd();
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, clearNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      const res = await pw9233Api.search(keyword.trim());
      if ((res.data || []).length === 0) {
        message.info('查無資料');
        return;
      }
      const keys = (res.data || []).map(r => `${r.f9430_01}|${r.f9430_02}`);
      setCurrentIdx(0);
      const [empCode, yearMonth] = parseKey(keys[0]);
      await loadRecordWithNames(empCode, yearMonth);
    } catch {
      message.error('查詢失敗');
    }
  }, [setCurrentIdx, loadRecordWithNames]);

  // -- Search state --
  const [searchText, setSearchText] = React.useState('');

  return (
    <PermissionGuard module={Modules.PW9233} action="query">
      <Spin spinning={loading}>
        <div className="pw9233-form">
          <div className="page-wrapper">

            {/* Title */}
            <div className="page-title-bar">
              <h1>非固定薪資輸入 (pw9233f1)</h1>
              <span className="nav-info">
                {allKeys.length > 0
                  ? `${currentIdx + 1} / ${allKeys.length}`
                  : '無資料'}
              </span>
            </div>

            {/* Action Bar */}
            <ActionBar
              mode={mode}
              isEditing={isEditing}
              hasRecord={!!record}
              currentIdx={currentIdx}
              allKeysLength={allKeys.length}
              permissions={perms}
              onAdd={handleAdd}
              onModify={handleModify}
              onDelete={handleDeleteRecord}
              onNavTo={handleNavTo}
              onExit={() => navigate('/erp')}
            />

            {/* Search Bar */}
            <div className="search-bar">
              <Input
                placeholder="輸入員工代號或年月查詢"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
                disabled={isEditing}
                style={{ width: 240 }}
                maxLength={20}
                suffix={<SearchOutlined />}
              />
              <button
                className="btn"
                disabled={isEditing}
                onClick={() => handleSearch(searchText)}
              >
                查詢
              </button>
              <button
                className="btn"
                disabled={isEditing}
                onClick={async () => {
                  setSearchText('');
                  const keys = await loadList();
                  if (keys.length > 0) {
                    setCurrentIdx(0);
                    const [empCode, yearMonth] = parseKey(keys[0]);
                    await loadRecordWithNames(empCode, yearMonth);
                  }
                }}
              >
                全部
              </button>
            </div>

            {/* Form Content */}
            <div className="form-content">
              {/* Master Info */}
              <div className="section-divider">基本資料</div>
              <MasterInfo
                formData={formData}
                names={names}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
                getFieldError={getFieldError}
                mode={mode}
              />

              {/* Detail Grid */}
              <div className="section-divider" style={{ marginTop: 16 }}>薪資項目明細</div>
              <DetailGrid
                details={details}
                isEditing={isEditing}
                names={names}
                updateDetailField={updateDetailField}
                onAdd={addDetailRow}
                onDelete={deleteDetailRow}
                resolveSingleName={resolveSingleName}
              />
            </div>

          </div>
        </div>
      </Spin>
    </PermissionGuard>
  );
};

export default PW9233Form;
